import React, { useEffect, useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import { Download, FileSpreadsheet, RefreshCw, UploadCloud, Users } from 'lucide-react'
import toast from 'react-hot-toast'
import { teacherStudentService } from '../../services/teacher/TeacherStudentService'
import { TeacherStudentRecord, TeacherStudentImportItem } from '../../types/teacher'

const normalizeHeader = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

const headerAliases: Record<'email' | 'name' | 'school' | 'class' | 'phone' | 'notes', string[]> = {
  email: ['email', 'mail', 'gmail', 'contact'],
  name: ['ho ten', 'họ tên', 'name', 'fullname', 'full name'],
  school: ['truong', 'trường', 'school'],
  class: ['lop', 'lớp', 'class'],
  phone: ['so dien thoai', 'số điện thoại', 'phone', 'sdt', 'tel', 'telephone', 'mobile'],
  notes: ['ghi chu', 'ghi chú', 'notes', 'note']
}

const TeacherStudentManager: React.FC = () => {
  const [students, setStudents] = useState<TeacherStudentRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [importing, setImporting] = useState(false)
  const [statusFilter, setStatusFilter] = useState<'all' | 'linked' | 'invited'>('all')
  const [search, setSearch] = useState('')
  const [importSummary, setImportSummary] = useState<TeacherStudentImportItem[]>([])
  const [fileName, setFileName] = useState<string | null>(null)

  const filteredStudents = useMemo(() => {
    return students.filter((item) => {
      if (statusFilter !== 'all' && item.status !== statusFilter) return false
      if (!search.trim()) return true
      const keyword = search.toLowerCase()
      return (
        (item.studentName || '').toLowerCase().includes(keyword) ||
        (item.studentEmail || '').toLowerCase().includes(keyword) ||
        (item.className || '').toLowerCase().includes(keyword) ||
        (item.school || '').toLowerCase().includes(keyword)
      )
    })
  }, [students, statusFilter, search])

  const summary = useMemo(() => {
    return {
      total: students.length,
      linked: students.filter((s) => s.status === 'linked').length,
      invited: students.filter((s) => s.status === 'invited').length
    }
  }, [students])

  useEffect(() => {
    loadStudents()
  }, [])

  const loadStudents = async () => {
    try {
      setLoading(true)
      const response = await teacherStudentService.getStudents()
      setStudents(response.items)
    } catch (error: any) {
      console.error(error)
      toast.error(error?.message || 'Không thể tải danh sách học sinh.')
    } finally {
      setLoading(false)
    }
  }

  const findColumnIndex = (headers: string[], aliases: string[]) => {
    return headers.findIndex((header) => aliases.includes(normalizeHeader(header)))
  }

  const parseExcelFile = async (file: File): Promise<TeacherStudentImportItem[]> => {
    const buffer = await file.arrayBuffer()
    const workbook = XLSX.read(buffer, { type: 'array' })

    if (!workbook.SheetNames.length) {
      throw new Error('File Excel không chứa sheet nào.')
    }

    const sheet = workbook.Sheets[workbook.SheetNames[0]]
    const rows = XLSX.utils.sheet_to_json<(string | number)[]>((sheet as any), {
      header: 1,
      defval: ''
    })

    if (!rows.length) {
      throw new Error('Sheet đầu tiên đang trống.')
    }

    const headerRow = rows[0].map((cell) => normalizeHeader(String(cell ?? '')))
    const emailIdx = findColumnIndex(headerRow, headerAliases.email)

    if (emailIdx === -1) {
      throw new Error('File phải có cột Email (ví dụ: "Email").')
    }

    const nameIdx = findColumnIndex(headerRow, headerAliases.name)
    const schoolIdx = findColumnIndex(headerRow, headerAliases.school)
    const classIdx = findColumnIndex(headerRow, headerAliases.class)
    const phoneIdx = findColumnIndex(headerRow, headerAliases.phone)
    const notesIdx = findColumnIndex(headerRow, headerAliases.notes)

    const data: TeacherStudentImportItem[] = []

    rows.slice(1).forEach((row, rowIndex) => {
      const email = (row[emailIdx] || '').toString().trim()
      if (!email) {
        return
      }

      const entry: TeacherStudentImportItem = {
        email,
        name: nameIdx !== -1 ? (row[nameIdx] || '').toString().trim() : undefined,
        school: schoolIdx !== -1 ? (row[schoolIdx] || '').toString().trim() : undefined,
        class: classIdx !== -1 ? (row[classIdx] || '').toString().trim() : undefined,
        phone: phoneIdx !== -1 ? (row[phoneIdx] || '').toString().trim() : undefined,
        notes: notesIdx !== -1 ? (row[notesIdx] || '').toString().trim() : undefined
      }

      data.push(entry)
    })

    if (!data.length) {
      throw new Error('Không có dòng dữ liệu hợp lệ sau khi đọc file.')
    }

    return data
  }

  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      setImporting(true)
      setFileName(file.name)

      const studentsToImport = await parseExcelFile(file)
      const response = await teacherStudentService.importStudents(studentsToImport)

      setImportSummary(studentsToImport)

      if (response.errors.length) {
        toast.error(`Một số dòng không hợp lệ (${response.errors.length}).`)
      } else {
        toast.success('Upload danh sách thành công!')
      }

      await loadStudents()
    } catch (error: any) {
      console.error(error)
      toast.error(error?.message || 'Không thể xử lý file Excel.')
    } finally {
      setImporting(false)
      event.target.value = ''
    }
  }

  const downloadTemplate = () => {
    const data = [
      ['Họ tên', 'Email', 'Trường', 'Lớp', 'Số điện thoại', 'Ghi chú'],
      ['Nguyễn Văn A', 'hocvien@example.com', 'THPT Demo', '12A1', '0901234567', ''],
      ['']
    ]
    const worksheet = XLSX.utils.aoa_to_sheet(data)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'DanhSach')
    XLSX.writeFile(workbook, 'mau-danh-sach-hoc-sinh.xlsx')
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm text-gray-500">Quản lý học sinh theo giáo viên</p>
          <h1 className="text-2xl font-semibold text-gray-900">Danh sách học sinh</h1>
        </div>
        <button
          onClick={loadStudents}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Làm mới</span>
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Tổng số học sinh</span>
            <Users className="w-5 h-5 text-blue-500" />
          </div>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{summary.total}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Đã liên kết</span>
            <Users className="w-5 h-5 text-green-500" />
          </div>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{summary.linked}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Chờ học sinh</span>
            <Users className="w-5 h-5 text-amber-500" />
          </div>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{summary.invited}</p>
        </div>
      </div>

      <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Upload danh sách Excel</h2>
            <p className="text-sm text-gray-600">
              File cần có các cột: Họ tên, Email, Trường, Lớp, Số điện thoại (tiêu đề dòng đầu tiên)
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={downloadTemplate}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              <span>Tải file mẫu</span>
            </button>
            <label className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
              <UploadCloud className="w-4 h-4" />
              <span>{importing ? 'Đang xử lý...' : 'Chọn file Excel'}</span>
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                disabled={importing}
                onChange={handleExcelUpload}
              />
            </label>
          </div>
        </div>
        {fileName && (
          <p className="mt-3 text-sm text-gray-600">
            File gần nhất: <span className="font-medium">{fileName}</span>
          </p>
        )}
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="linked">Đã liên kết</option>
            <option value="invited">Chờ học sinh</option>
          </select>
          <div className="relative">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm theo tên, email, lớp..."
              className="border border-gray-300 rounded-lg pl-3 pr-3 py-2 text-sm w-64"
            />
          </div>
        </div>
        <span className="text-sm text-gray-500">Hiển thị {filteredStudents.length} học sinh</span>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Họ tên
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Trường
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Lớp
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Số điện thoại
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Trạng thái
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-sm">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                  Đang tải danh sách...
                </td>
              </tr>
            ) : filteredStudents.length ? (
              filteredStudents.map((student) => (
                <tr key={student.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{student.studentName || '—'}</td>
                  <td className="px-4 py-3 text-gray-700">{student.studentEmail}</td>
                  <td className="px-4 py-3 text-gray-700">{student.school || '—'}</td>
                  <td className="px-4 py-3 text-gray-700">{student.className || '—'}</td>
                  <td className="px-4 py-3 text-gray-700">{student.phone || '—'}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        student.status === 'linked'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {student.status === 'linked' ? 'Đã liên kết' : 'Chờ học sinh'}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                  Chưa có học sinh nào trong danh sách.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {importSummary.length > 0 && (
        <div className="mt-8 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Lần upload gần nhất</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Đã đọc {importSummary.length} dòng hợp lệ từ file Excel. Hệ thống tự động cập nhật những học sinh
            đã có, thêm mới phần còn lại.
          </p>
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600">Họ tên</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600">Email</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600">Trường</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600">Lớp</th>
                </tr>
              </thead>
              <tbody>
                {importSummary.slice(0, 10).map((item, index) => (
                  <tr key={`${item.email}-${index}`} className="border-t text-gray-700">
                    <td className="px-4 py-2">{item.name || '—'}</td>
                    <td className="px-4 py-2">{item.email}</td>
                    <td className="px-4 py-2">{item.school || '—'}</td>
                    <td className="px-4 py-2">{item.class || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {importSummary.length > 10 && (
            <p className="text-sm text-gray-500 mt-2">... và {importSummary.length - 10} dòng khác</p>
          )}
        </div>
      )}
    </div>
  )
}

export default TeacherStudentManager

